import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './about.css';
import Header from '../components/layout/header';
import Footer from '../components/layout/footer';
import StatCard from '../components/common/StatCard';
import FAQ from '../components/layout/FAQ';
import ContactBanner from '../components/layout/ContactBanner';
import Loader from '../components/layout/preloader';
import { supabase } from '../supabaseClient';

import img1 from '../asserts/img1.jpg';
import img2 from '../asserts/img2.jpg';
import img3 from '../asserts/img3.jpg';
import img4 from '../asserts/img4.jpg';
import img5 from '../asserts/img5.jpg';
import img6 from '../asserts/img6.jpg';
import img7 from '../asserts/img7.jpg';
import img8 from '../asserts/img8.jpg';
import img9 from '../asserts/img9.jpg';

gsap.registerPlugin(ScrollTrigger);

const sliderImages = [img1, img2, img3, img4, img5, img6, img7, img8, img9];

const ImageSlider = React.memo(() => {
  const [current, setCurrent] = useState(0);
  const total = sliderImages.length;
  const sliderRef = useRef();
  const intervalRef = useRef();

  const visibleImages = useMemo(() => {
    const imgs = [];
    for (let i = 0; i < 3; i++) {
      imgs.push(sliderImages[(current + i) % total]);
    }
    return imgs;
  }, [current, total]);

  const nextImage = useMemo(() => {
    return sliderImages[(current + 3) % total];
  }, [current, total]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    link.href = nextImage;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [nextImage]);

  const advance = useCallback(() => {
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  useEffect(() => {
    intervalRef.current = setInterval(advance, 4500);
    return () => clearInterval(intervalRef.current);
  }, [advance]);

  useEffect(() => {
    gsap.fromTo(sliderRef.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: { trigger: sliderRef.current, start: 'top 85%' }
      }
    );
  }, []);

  const handleDotClick = useCallback((i) => {
    setCurrent(i);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(advance, 4500);
  }, [advance]);

  return (
    <div className="slider-section" ref={sliderRef}>
      <div className="slider-viewport">
        <div className="slider-track">
          {visibleImages.map((img, i) => (
            <div className="slider-card" key={`${current}-${i}`}>
              <img src={img} alt={`Interior design showcase ${current + i + 1}`} className="slider-img" loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </div>
      <div className="slider-dots" role="tablist" aria-label="Image slider controls">
        {sliderImages.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === current}
            aria-label={`Go to slide ${i + 1}`}
            className={`slider-dot${i === current ? ' slider-dot-active' : ''}`}
            onClick={() => handleDotClick(i)}
          />
        ))}
      </div>
    </div>
  );
});

const About = () => {
  const PRELOADER = 4.1;

  const [content, setContent] = useState(null);
  const [stats, setStats] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cto, setCto] = useState(null);
  const [dataReady, setDataReady] = useState(false);

  const abtRef = useRef();
  const titleRef = useRef();
  const text1Ref = useRef();
  const text2Ref = useRef();
  const text3Ref = useRef();
  const stat1Ref = useRef();
  const stat2Ref = useRef();
  const stat3Ref = useRef();
  const faqRef = useRef();
  const contactRef = useRef();

  useEffect(() => {
    const fetchAll = async () => {
      const [contentRes, statsRes, faqsRes, ctoRes] = await Promise.all([
        supabase.from('about_section_content').select('*').single(),
        supabase.from('about_section_stats').select('*').order('display_order'),
        supabase.from('faq_section').select('*').order('display_order'),
        supabase.from('about_section_cto').select('*').single(),
      ]);
      setContent(contentRes.data || null);
      setStats(statsRes.data || []);
      setFaqs(faqsRes.data || []);
      setCto(ctoRes.data || null);
      setDataReady(true);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {

      if (abtRef.current) {
        gsap.fromTo(abtRef.current,
          { y: 100, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: PRELOADER }
        );
      }

      if (titleRef.current) {
        gsap.fromTo(titleRef.current,
          { opacity: 0, y: -30 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: PRELOADER + 0.2 }
        );
      }

      const textEls = [text1Ref.current, text2Ref.current, text3Ref.current].filter(Boolean);
      if (textEls.length) {
        gsap.fromTo(textEls,
          { opacity: 0, x: -30 },
          { opacity: 1, x: 0, duration: 0.7, stagger: 0.15, ease: 'power2.out', delay: PRELOADER + 0.4 }
        );
      }

      [stat1Ref, stat2Ref, stat3Ref].forEach((r, i) => {
        if (r.current) {
          gsap.fromTo(r.current,
            { scale: 0.8, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.5, ease: 'power2.out', delay: PRELOADER + 0.6 + i * 0.15 }
          );
        }
      });

      gsap.fromTo('.faq-item',
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out', scrollTrigger: { trigger: faqRef.current, start: 'top 80%' } }
      );

      if (contactRef.current) {
        gsap.fromTo(contactRef.current,
          { y: -60, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out', scrollTrigger: { trigger: contactRef.current, start: 'top 85%' } }
        );
      }

    });

    return () => ctx.revert();
  }, [dataReady]);

  const statRefs = [stat1Ref, stat2Ref, stat3Ref];

  return (
    <>
      <Loader />
      <Header />

      <main>
        <section className="about-section">
          <div className="abt" ref={abtRef}>
            <div className="abt-inner">
              <h1 className="abt-title" ref={titleRef}>{content?.main_title_en || ''}</h1>

              <div className="abt-content">
                <p className="abt-text" ref={text1Ref}>{content?.para_one_en || ''}</p>
                <p className="abt-text" ref={text2Ref}>{content?.para_two_en || ''}</p>
                <p className="abt-text" ref={text3Ref}>{content?.para_three_en || ''}</p>
              </div>

              <div className="abt-stats">
                {stats.map((stat, i) => (
                  <div key={stat.id} ref={statRefs[i]}>
                    <StatCard number={stat.stat_value} label={stat.stat_label_en} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <ImageSlider />

        <div ref={faqRef}>
          <FAQ faqs={faqs} />
        </div>

        <div ref={contactRef}>
          <ContactBanner cto={cto} />
        </div>
      </main>

      <Footer />
    </>
  );
};

export default About;